import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Heart, MessageCircle, Star, Plus, Filter, X, Image, Send, User, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const POSTS_PER_PAGE = 25;
const MAX_LOADED_POSTS = 200; // Prevent memory issues

const ForumPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const observerRef = useRef();
  const loadMoreRef = useRef();

  const filterOptions = [
    { value: 'all', label: 'All Posts' },
    ...(token ? [{ value: 'my_posts', label: 'My Posts' }] : []),
    { value: 'ratings', label: 'Ratings' },
    { value: 'comments', label: 'Comments' },
    { value: 'new_cigars', label: 'New Cigars' },
    { value: 'manual', label: 'Discussions' },
  ];

  const fetchPosts = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPosts([]);
      } else {
        setLoadingMore(true);
      }

      const skip = reset ? 0 : posts.length;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/forum/posts`, {
        params: {
          skip,
          limit: POSTS_PER_PAGE,
          filter_type: filter === 'all' ? null : filter
        },
        headers
      });

      const newPosts = res.data.posts;
      
      if (reset) {
        setPosts(newPosts);
      } else {
        // Prevent loading too many posts (memory management)
        setPosts(prev => {
          const combined = [...prev, ...newPosts];
          if (combined.length > MAX_LOADED_POSTS) {
            // Keep only the most recent posts
            return combined.slice(0, MAX_LOADED_POSTS);
          }
          return combined;
        });
      }
      
      setHasMore(res.data.has_more);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token, filter, posts.length]);

  // Initial load and filter change
  useEffect(() => {
    fetchPosts(true);
  }, [token, filter]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchPosts(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, loadingMore, hasMore, fetchPosts]);

  const handleLike = async (postId, isLiked) => {
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    
    try {
      if (isLiked) {
        await axios.delete(`${API}/forum/posts/${postId}/like`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/forum/posts/${postId}/like`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Update local state
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            liked_by_user: !isLiked,
            likes_count: isLiked ? post.likes_count - 1 : post.likes_count + 1
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const toggleComments = async (postId) => {
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
      return;
    }

    // Fetch comments if not already loaded
    if (!postComments[postId]) {
      try {
        const res = await axios.get(`${API}/forum/posts/${postId}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPostComments(prev => ({ ...prev, [postId]: res.data.comments }));
      } catch (error) {
        toast.error('Failed to load comments');
        return;
      }
    }

    setExpandedComments(prev => ({ ...prev, [postId]: true }));
  };

  const handleSubmitComment = async (postId) => {
    const content = commentInputs[postId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.post(`${API}/forum/posts/${postId}/comments`, 
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add new comment to local state
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.data]
      }));
      
      // Update comment count on post
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return { ...post, comments_count: post.comments_count + 1 };
        }
        return post;
      }));
      
      // Clear input
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove post from local state
      setPosts(prev => prev.filter(post => post.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const getPostTypeIcon = (postType) => {
    switch (postType) {
      case 'rating': return <Star className="text-amber-500" size={14} />;
      case 'comment': return <MessageCircle className="text-blue-400" size={14} />;
      case 'new_cigar': return <Plus className="text-green-400" size={14} />;
      default: return <MessageSquare className="text-white/50" size={14} />;
    }
  };

  const handleCreatePost = () => {
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    setShowCreateModal(true);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0C]/95 backdrop-blur-sm z-10 px-4 py-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Forum</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={`p-2 rounded-full transition-colors ${filter !== 'all' ? 'bg-amber-500/20 text-amber-500' : 'text-white/50 hover:text-white'}`}
            >
              <Filter size={20} />
            </button>
            <button
              data-testid="create-post-button"
              onClick={handleCreatePost}
              className="bg-amber-600 hover:bg-amber-700 text-white p-2 rounded-full"
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {/* Filter Menu */}
        {showFilterMenu && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filterOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  setFilter(opt.value);
                  setShowFilterMenu(false);
                }}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  filter === opt.value
                    ? 'bg-amber-600 text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Posts */}
      <div className="max-w-2xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquare size={48} className="text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No posts yet</p>
            <p className="text-white/30 text-sm mt-2">Be the first to share something!</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {posts.map(post => (
              <div key={post.id} className="p-4">
                {/* Post Header */}
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/user/${post.user_id}`)}
                  >
                    {post.user_profile_pic ? (
                      <img src={post.user_profile_pic} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={20} className="text-white/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span 
                        className="font-medium text-white cursor-pointer hover:text-amber-500"
                        onClick={() => navigate(`/user/${post.user_id}`)}
                      >
                        {post.username}
                      </span>
                      {getPostTypeIcon(post.post_type)}
                      <span className="text-white/40 text-sm">{formatTime(post.created_at)}</span>
                    </div>
                    <p className="text-white/80 mt-1">{post.content}</p>
                    
                    {/* Rating Badge */}
                    {post.rating_value && (
                      <div className="inline-flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded-full mt-2">
                        <Star className="text-amber-500 fill-amber-500" size={14} />
                        <span className="text-amber-500 font-bold text-sm">{post.rating_value.toFixed(1)}</span>
                      </div>
                    )}
                    
                    {/* Cigar Card */}
                    {post.cigar_id && (
                      <div 
                        className="mt-3 bg-white/5 rounded-lg p-3 flex gap-3 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => navigate(`/cigar/${post.cigar_id}`)}
                      >
                        {post.cigar_image && (
                          <div className="w-16 h-16 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
                            <img src={post.cigar_image} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div>
                          <p className="text-white/50 text-xs">{post.cigar_brand}</p>
                          <p className="text-white font-medium">{post.cigar_name}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Post Images */}
                    {post.images && post.images.length > 0 && (
                      <div className={`mt-3 grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : post.images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {post.images.map((img, idx) => (
                          <div key={idx} className="rounded-lg overflow-hidden bg-white/10 aspect-square">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-6 mt-3 pl-13">
                  <button
                    data-testid={`forum-page-like-${post.id}`}
                    onClick={() => handleLike(post.id, post.liked_by_user)}
                    className={`flex items-center gap-1.5 transition-colors ${
                      post.liked_by_user ? 'text-red-500' : 'text-white/40 hover:text-red-400'
                    }`}
                  >
                    <Heart size={18} className={post.liked_by_user ? 'fill-current' : ''} />
                    <span className="text-sm">{post.likes_count || 0}</span>
                  </button>
                  <button
                    data-testid={`forum-page-comments-${post.id}`}
                    onClick={() => toggleComments(post.id)}
                    className="flex items-center gap-1.5 text-white/40 hover:text-white transition-colors"
                  >
                    <MessageCircle size={18} />
                    <span className="text-sm">{post.comments_count || 0}</span>
                  </button>
                  {/* Delete button - only for post owner */}
                  {user && post.user_id === user.id && (
                    <button
                      data-testid={`forum-page-delete-${post.id}`}
                      onClick={() => handleDeletePost(post.id)}
                      className="flex items-center gap-1.5 text-white/40 hover:text-red-500 transition-colors ml-auto"
                      title="Delete post"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                {/* Comments Section */}
                {expandedComments[post.id] && (
                  <div className="mt-3 pl-13 space-y-3">
                    {/* Existing Comments */}
                    {postComments[post.id]?.map(comment => (
                      <div key={comment.id} className="flex gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {comment.user_profile_pic ? (
                            <img src={comment.user_profile_pic} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User size={14} className="text-white/50" />
                          )}
                        </div>
                        <div className="flex-1 bg-white/5 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">{comment.username}</span>
                            <span className="text-white/30 text-xs">{formatTime(comment.created_at)}</span>
                          </div>
                          <p className="text-white/70 text-sm mt-0.5">{comment.content}</p>
                        </div>
                      </div>
                    ))}

                    {/* Comment Input */}
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {user?.profile_pic ? (
                          <img src={user.profile_pic} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={14} className="text-white/50" />
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          placeholder="Write a comment..."
                          value={commentInputs[post.id] || ''}
                          onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                          onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment(post.id)}
                          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/30"
                        />
                        <button
                          onClick={() => handleSubmitComment(post.id)}
                          disabled={!commentInputs[post.id]?.trim() || submittingComment[post.id]}
                          className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white p-2 rounded-full"
                        >
                          {submittingComment[post.id] ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Load More Trigger */}
        {hasMore && !loading && (
          <div ref={loadMoreRef} className="py-8 flex justify-center">
            {loadingMore && <Loader2 className="animate-spin text-amber-500" size={24} />}
          </div>
        )}

        {/* End Message */}
        {!hasMore && posts.length > 0 && (
          <div className="py-8 text-center text-white/30 text-sm">
            You've reached the end
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal 
          onClose={() => setShowCreateModal(false)} 
          onPostCreated={(newPost) => {
            setPosts(prev => [newPost, ...prev]);
            setShowCreateModal(false);
          }}
          token={token}
          user={user}
        />
      )}

      <BottomNav />
    </div>
  );
};

// Create Post Modal Component
const CreatePostModal = ({ onClose, onPostCreated, token, user }) => {
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef();

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        // Convert to base64 for preview and upload
        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64 = event.target.result;
          
          // Upload to backend
          try {
            const formData = new FormData();
            formData.append('file', file);
            
            const res = await axios.post(`${API}/profile/upload-image`, formData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            setImages(prev => [...prev, res.data.url]);
          } catch (error) {
            // Fallback to base64 if upload fails
            setImages(prev => [...prev, base64]);
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toast.error('Failed to process images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please write something');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forum/posts`, 
        { content: content.trim(), images },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Post created!');
      onPostCreated(res.data);
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#141416] w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Create Post</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.profile_pic ? (
                <img src={user.profile_pic} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={20} className="text-white/50" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-white font-medium mb-2">{user?.username}</p>
              <textarea
                placeholder="What's on your mind?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                className="w-full bg-transparent text-white placeholder:text-white/30 resize-none outline-none"
              />
            </div>
          </div>

          {/* Image Previews */}
          {images.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {images.map((img, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={images.length >= 3 || uploading}
            className="flex items-center gap-2 text-white/50 hover:text-white disabled:opacity-30"
          >
            <Image size={20} />
            <span className="text-sm">{images.length}/3</span>
          </button>
          <Button
            onClick={handleSubmit}
            disabled={!content.trim() || submitting}
            className="bg-amber-600 hover:bg-amber-700 px-6"
          >
            {submitting ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForumPage;
